//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import {
  CreateSamplingCampaignRequestResponse,
  DeleteSamplingCampaignRequestPayload,
  EditSamplingCampaignRequestPayload,
  EditSamplingCampaignRequestResponse,
  GetClientSamplingCampaignRequestByIdRequest,
  GetClientSamplingCampaignRequestsRequest,
  GetSamplingCampaignRequestQuoteRequest,
  GetSamplingCampaignRequestQuoteResponse,
  GetSamplingCampaignRequestResponse,
  GetSamplingCampaignRequestsResponse,
  RequestCampaignRequestQuoteRequest,
  SamplingCampaignRequest,
  SamplingCampaignRequestQuote,
  SamplingCampaignRequestsResponse,
  TransformBulkUploadProductsQueryParams,
  TransformBulkUploadProductsRequestPayload,
  TransformBulkUploadProductsResponse,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import {
  deserializeCampaignRequest,
  deserializeCampaignRequestReduced,
  deserializeProduct,
  serializeCampaignRequest,
} from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiUtils';
import { BVSamplingProduct } from '@apis/campaign-requests/sampling/products/samplingProductsApiModel';
import { makeParams } from '@utils/makeParams';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

const CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT = '/api/v1/clients/campaign-requests';

export const BACKEND_ERROR_MESSAGES = {
  MISSING_BV_CLIENT_ID: 'Missing BV client ID for SoCo client ID',
};

const campaignRequestsFetch = (
  endpoint: string,
  init?: RequestInit,
): Promise<ISerializedResponse> =>
  serializedGatewayFetch(
    'sampling_gateway',
    `${CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT}${endpoint}`,
    init,
  );

const samplingProductsFetch = (
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> => {
  return serializedGatewayFetch(
    'sampling_gateway',
    `${CAMPAIGN_REQUESTS_DEFAULT_ENDPOINT}/products${endpoint}`,
    init,
    useFetchHeader,
  );
};

/**
 * Gets all campaigns request for a specific client
 *
 * GET /api/v1/campaign-requests
 */
export const getClientCampaignRequests = async (
  req: GetClientSamplingCampaignRequestsRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequestsResponse> => {
  const params = makeParams(req);

  const res = await campaignRequestsFetch(`?${params}`, {
    ...init,
    method: 'GET',
  });

  const campaignRequestsResponse =
    await res.json<GetSamplingCampaignRequestsResponse>((res) => ({
      data: {
        ...res.data,
        campaignRequests: res.data.campaignRequests.map((campaign) =>
          deserializeCampaignRequestReduced(campaign),
        ),
      },
    }));

  return campaignRequestsResponse.data;
};

/**
 * Gets a campaign request for a specific client
 *
 * GET /api/v1/campaign-requests/:campaignId
 */

export const getClientCampaignRequest = async (
  req: GetClientSamplingCampaignRequestByIdRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const res = await campaignRequestsFetch(`/${req.campaignId}`, {
    ...init,
    method: 'GET',
  });

  const campaignRequestsResponse =
    await res.json<GetSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  // Clean up deprecated sendToMeAndOthers value
  if (
    campaignRequestsResponse.data.fulfillmentOptions?.sendTo ===
    'sendToMeAndOthers'
  ) {
    campaignRequestsResponse.data.fulfillmentOptions.sendTo = 'sendToOther';
  }

  // Clean up multiple emails
  if (
    campaignRequestsResponse.data.fulfillmentOptions?.emails &&
    campaignRequestsResponse.data.fulfillmentOptions.emails.length > 1 &&
    campaignRequestsResponse.data.fulfillmentOptions.emails[0]
  ) {
    campaignRequestsResponse.data.fulfillmentOptions.emails = [
      campaignRequestsResponse.data.fulfillmentOptions.emails[0],
    ];
  }

  return campaignRequestsResponse.data;
};

/**
 * Creates a new campaign request for a specific client
 *
 * POST /api/v1/campaign-requests
 */
export const createClientCampaignRequest = async (
  req: SamplingCampaignRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const res = await campaignRequestsFetch('', {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req),
  });

  const createSamplingCampaignRequestResponse =
    await res.json<CreateSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  return createSamplingCampaignRequestResponse.data;
};

/**
 * Edit a campaign request for a specific client
 *
 * PATCH /api/v1/clients/campaign-requests/:id/
 */
export const editClientCampaignRequest = async (
  req: EditSamplingCampaignRequestPayload,
  init?: RequestInit,
): Promise<SamplingCampaignRequest> => {
  const serializedCampaign = serializeCampaignRequest(req.campaign);

  const res = await campaignRequestsFetch(`/${req.id}`, {
    ...init,
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(serializedCampaign),
  });

  const editSamplingCampaignRequestResponse =
    await res.json<EditSamplingCampaignRequestResponse>((res) => ({
      data: deserializeCampaignRequest(res.data),
    }));

  return editSamplingCampaignRequestResponse.data;
};

/**
 * Delete a campaign request for a specific client
 *
 * DELETE /api/v1/clients/campaign-requests/:id/
 */
export const deleteClientCampaignRequest = async (
  req: DeleteSamplingCampaignRequestPayload,
  init?: RequestInit,
): Promise<void> => {
  await campaignRequestsFetch(`/${req.id}`, {
    ...init,
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
    },
  });
};

/**
 * Retrieve the current quote for a campaign request (read-only).
 *
 * GET /api/v1/clients/campaign-requests/:campaignRequestId/quote
 */
export const getCampaignRequestQuote = async (
  req: GetSamplingCampaignRequestQuoteRequest,
  init?: RequestInit,
): Promise<SamplingCampaignRequestQuote> => {
  const res = await campaignRequestsFetch(`/${req.campaignRequestId}/quote`, {
    ...init,
    method: 'GET',
  });

  const response = await res.json<GetSamplingCampaignRequestQuoteResponse>();
  return response.data;
};

/**
 * Request (or re-request) a quote for a campaign request (side-effecting).
 * Triggers asynchronous quote generation; this does not return the quote.
 *
 * POST /api/v1/clients/campaign-requests/:campaignRequestId/quote
 */
export const requestQuote = async (
  req: RequestCampaignRequestQuoteRequest,
  init?: RequestInit,
): Promise<void> => {
  await campaignRequestsFetch(`/${req.campaignRequestId}/quote`, {
    ...init,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(req.payload),
  });
};

/**
 * Transform a CSV/XLSX file into product data
 *
 * POST /api/v1/clients/campaign-requests/products/transform
 */
export const transformBulkUploadProducts = async (
  req: TransformBulkUploadProductsRequestPayload,
  queryParams?: TransformBulkUploadProductsQueryParams,
  init?: RequestInit,
): Promise<BVSamplingProduct[]> => {
  const formData = new FormData();
  formData.append('mediaItem', req.file, req.file.name);

  const params = makeParams(queryParams);
  const res = await samplingProductsFetch(
    `/transform${params ? `?${params}` : ''}`,
    {
      ...init,
      method: 'POST',
      body: formData,
    },
    true,
  );

  const response = await res.json<TransformBulkUploadProductsResponse>();
  return response.data.map(deserializeProduct);
};
